import { Employee } from "domain/entities/employee";
import { Parameter } from "domain/entities/parameter";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { VacationInputFilter } from "domain/models/vacation-input-filter";

import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import Spinner from "presentation/components/Shared/Spinner";

import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useVacationPlanByFilter } from "presentation/hooks/use-query-vacation";

import { useMemo, useState } from "react";
import "./index.css"
import { Vacation } from "domain/entities/vacations";
import { useTimesheetDaysByFilter } from "presentation/hooks/use-query-timesheet-day";
import { TimesheetDayInputFilter } from "domain/models/timesheet-day-input-filter";

interface EmployeeInTableFormat {
  employeeNumber: number;
  employeeName: string;

  daysAlreadyTakenLastYear?: number;
  daysAlreadyTakenThisYear?: number;
  daysAlreadyTakenNextYear?: number;

  daysStillToTakeLastYear?: number;
  daysStillToTakeThisYear?: number;

  totalLegalDaysCurrentYear?: number;
  totalLegalDaysLastYear?: number;
  totalLegalDaysNextYear?: number;
}

export const VacationAnalysis: React.FC = () => {

  const currentYear: number = new Date().getFullYear();

  const [employeeIdToCheck, setEmployeeIdToCheck] =
    useState<string | undefined>(undefined);

  const {
    data: employeesData,
    isLoading: isEmployeesLoading,
  } = useEmployeesByFilter({
    limit: 200,
    hasTerminationDateInLastTwoMonths: true,
  } as EmployeeInputFilter);


  const selectedEmployee = useMemo((): Employee | undefined => {
    if (!employeesData?.data || employeeIdToCheck === undefined) return undefined;

    return employeesData.data.find(emp => emp.id === employeeIdToCheck);
  }, [employeesData, employeeIdToCheck]);


  const selectedEmployeeRefDay = useMemo((): Date | undefined => {
    if (!selectedEmployee) return undefined;

    const refDay = selectedEmployee.terminationDate
      ? new Date(selectedEmployee.terminationDate)
      : new Date();

    refDay.setHours(12, 0, 0, 0);
    return refDay;
  }, [selectedEmployee]);

  const monthToCheck = selectedEmployeeRefDay
    ? selectedEmployeeRefDay.getMonth() + 1
    : undefined;

  const yearToCheck = selectedEmployeeRefDay
    ? selectedEmployeeRefDay.getFullYear()
    : undefined;

  const formatedEmployeesForDropdown: Parameter[] = useMemo(() => {
    return employeesData?.data?.map((item: Employee): Parameter => ({
      id: item.id,
      value: `${item.name} (${item.employeeNumber})`,
      type: "employee",
    })) ?? [];
  }, [employeesData]);

  const {
    data: vacationData,
    isLoading: isVacationLoading,
    isRefetching: isVacationRefetching,
  } = useVacationPlanByFilter({
    limit: 3000,
    startRange: new Date(currentYear, 0, 1, 0, 0, 0, 0),
    endRange: new Date(currentYear, 11, 31, 23, 59, 59, 999),
    allExceptCancelled: true,
    employeeId: employeeIdToCheck,
  } as VacationInputFilter,
    employeeIdToCheck !== undefined
  );

  const {
    data: vacationLastYearData,
    isLoading: isVacationLastYearLoading,
    isRefetching: isVacationLastYearRefetching,
  } = useVacationPlanByFilter({
    limit: 3000,
    startRange: new Date(currentYear - 1, 0, 1, 0, 0, 0, 0),
    endRange: new Date(currentYear - 1, 11, 31, 23, 59, 59, 999),
    allExceptCancelled: true,
    employeeId: employeeIdToCheck,
  } as VacationInputFilter,
    employeeIdToCheck !== undefined
  );

  const {
    data: timesheetDaysData,
    isLoading: isTimesheetDaysDataLoading,
    isRefetching: isTimesheetDaysDataRefetching,
  } = useTimesheetDaysByFilter({
    limit: 1000,
    abscenteTypeId: "2ac89ee6-3234-4fcc-93e1-1eb3af67f0ce",
    employeeId: employeeIdToCheck,
    month: monthToCheck,
    year: yearToCheck,
  } as TimesheetDayInputFilter,
    employeeIdToCheck !== undefined &&
    monthToCheck !== undefined &&
    yearToCheck !== undefined
  );

  const calculateVacationDaysPerYear = (vacationPlan: Vacation[]): { daysLastYear: number, daysCurrentYear: number } => {
    const daysLastYear =
      vacationPlan.reduce((total, vacPlan) => total + (vacPlan.daysLastYear ?? 0), 0);

    const daysThisYear =
      vacationPlan.reduce((total, vacPlan) => total + (vacPlan.daysCurrentYear ?? 0), 0);

    return {
      daysLastYear: daysLastYear,
      daysCurrentYear: daysThisYear,
    };
  }

  const fullMonthsBetween = (start: Date, end: Date): number => {
    let months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());

    if (end.getDate() < start.getDate()) {
      months--;
    }

    return Math.max(0, months);
  }


  const calculateVacationDays = (emp: Employee, refDay: Date): EmployeeInTableFormat | undefined => {

    const employeeVacations = vacationData?.data?.filter(
      vacPlan => vacPlan.employee.id === emp.id
    );

    const vacationsToConsider = employeeVacations?.filter(
      vacPlan => vacPlan.status?.toLowerCase() != "a aprovar"
    );

    const pastVacations = vacationsToConsider?.filter(
      vacPlan => new Date(vacPlan.endAt!) < refDay
    );

    const {
      daysLastYear: daysAlreadyTakenLastYear,
      daysCurrentYear: daysAlreadyTakenThisYear,
    } = calculateVacationDaysPerYear(pastVacations ?? []);

    const currentVacations = vacationsToConsider?.filter(
      vacPlan =>
        new Date(vacPlan.startAt!) <= refDay &&
        new Date(vacPlan.endAt!) >= refDay
    );

    let currentVacationPastDayLastYear = 0;
    let currentVacationPastDayThisYear = 0;

    currentVacations?.forEach(vacPlan => {
      const startDate = new Date(vacPlan.startAt!);
      const endDate = new Date(new Date(vacPlan.endAt!).setHours(12, 0, 0, 0));
      let daysFromLastYear = vacPlan.daysLastYear ?? 0;
      let daysFromThisYear = vacPlan.daysCurrentYear ?? 0;

      for (
        let currentDay = new Date(new Date(startDate).setHours(12, 0, 0, 0));
        currentDay <= endDate;
        currentDay.setDate(currentDay.getDate() + 1)
      ) {
        const day = new Date(currentDay);

        if (!emp.isSuportService) {
          if (day.getDay() === 0 || day.getDay() === 6)
            continue;
        }

        const maxVersion = Math.max(
          ...(timesheetDaysData?.data
            ?.filter(tshd =>
              tshd?.timesheet?.employee?.id === emp.id &&
              tshd?.timesheet?.month === day.getMonth() + 1 &&
              tshd?.timesheet?.year === day.getFullYear()
            )
            ?.map(tshd => tshd.timesheet.version!) ?? [0])
        );

        const possibleHoliday = timesheetDaysData?.data?.filter(tshd =>
          tshd?.timesheet?.employee?.id === emp.id &&
          tshd?.timesheet?.version === maxVersion &&
          tshd?.timesheet?.status?.toLowerCase() !== "cancelado" &&
          tshd.day === day.getDate() &&
          tshd?.timesheet?.month === day.getMonth() + 1 &&
          tshd?.timesheet?.year === day.getFullYear()
        );

        if ((possibleHoliday?.length ?? 0) > 0)
          continue;

        if (day <= refDay) {
          if (daysFromLastYear > 0) {
            daysFromLastYear -= 1;
            currentVacationPastDayLastYear += 1;
          }
          else if (daysFromThisYear > 0) {
            daysFromThisYear -= 1;
            currentVacationPastDayThisYear += 1;
          }
        }
      }
    });

    const employeeLastYearVacations = vacationLastYearData?.data?.filter(
      vacPlan => vacPlan.employee.id === emp.id
    );
    const vacationsLastYearToConsider = employeeLastYearVacations?.filter(
      vacPlan => vacPlan.status?.toLowerCase() != "a aprovar"
    );
    const employeeLastYearVacationsDays = vacationsLastYearToConsider?.reduce(
      (acc, vacPlan) => acc + (vacPlan.daysCurrentYear ?? 0), 0
    );

    let isAfterApril = 1;

    const monthOfTermination = new Date(emp.terminationDate!).getMonth() + 1;
    let totalLegalDaysCurrentYear = 0;
    let totalLegalDaysLastYear = 0;

    let admissionDate = new Date(new Date(emp.admissionDate!).setHours(12, 0, 0, 0));
    let terminationDate = new Date(new Date(emp.terminationDate!).setHours(12, 0, 0, 0));

    let startDateToConsider = admissionDate;

    let limiarStartDateLastYear = new Date(terminationDate.getFullYear() - 1, 11, admissionDate.getDate(), 12, 0, 0, 0);
    if (admissionDate < limiarStartDateLastYear)
      startDateToConsider = new Date(terminationDate.getFullYear() - 1, 11, admissionDate.getDate(), 12, 0, 0, 0);

    totalLegalDaysCurrentYear = fullMonthsBetween(startDateToConsider, terminationDate) * 2;

    if (monthOfTermination <= 4 && (admissionDate <= limiarStartDateLastYear)) {

      isAfterApril = 0
      startDateToConsider = admissionDate;
      let endDateToConsider = new Date(terminationDate.getFullYear() - 1, 11, admissionDate.getDate());

      let limiarStartDateLastTwoYear = new Date(terminationDate.getFullYear() - 2, 11, admissionDate.getDate(), 12, 0, 0, 0);

      if (admissionDate < limiarStartDateLastTwoYear)
        startDateToConsider = new Date(terminationDate.getFullYear() - 2, 11, admissionDate.getDate(), 12, 0, 0, 0);

      totalLegalDaysLastYear = fullMonthsBetween(startDateToConsider, endDateToConsider) * 2;
    }

    return {
      employeeNumber: emp.employeeNumber,
      employeeName: emp.name,

      daysAlreadyTakenLastYear: isAfterApril ? 0 : (employeeLastYearVacationsDays ?? 0) + daysAlreadyTakenLastYear + currentVacationPastDayLastYear,
      daysAlreadyTakenThisYear: daysAlreadyTakenThisYear + currentVacationPastDayThisYear + ((emp.vacationDaysNextYearFromLastYear ?? 0) * (-1)),
      daysAlreadyTakenNextYear: ((emp.vacationDaysNextYear ?? 0) * (-1)),

      daysStillToTakeLastYear: isAfterApril ? 0 : (totalLegalDaysLastYear - (employeeLastYearVacationsDays ?? 0)) - (daysAlreadyTakenLastYear + currentVacationPastDayLastYear),
      daysStillToTakeThisYear: totalLegalDaysCurrentYear - (daysAlreadyTakenThisYear + currentVacationPastDayThisYear + ((emp.vacationDaysNextYearFromLastYear ?? 0) * (-1))),

      totalLegalDaysCurrentYear: totalLegalDaysCurrentYear,
      totalLegalDaysLastYear: totalLegalDaysLastYear,
      totalLegalDaysNextYear: 0,
    };
  };

  const isLoadingAnyQuery =
    isEmployeesLoading ||
    isVacationLoading || isVacationRefetching ||
    isVacationLastYearLoading || isVacationLastYearRefetching ||
    isTimesheetDaysDataLoading || isTimesheetDaysDataRefetching;

  const employeesInTableFormat: EmployeeInTableFormat[] = useMemo(() => {
    if (
      !selectedEmployee ||
      !selectedEmployeeRefDay ||
      isLoadingAnyQuery ||
      !vacationData?.metadata.dateReq ||
      !vacationLastYearData?.metadata.dateReq
    ) {
      return [];
    }

    const empToAdd = calculateVacationDays(selectedEmployee, selectedEmployeeRefDay);
    return empToAdd ? [empToAdd] : [];
  }, [
    selectedEmployee,
    selectedEmployeeRefDay,
    vacationData,
    vacationLastYearData,
    timesheetDaysData,
    isLoadingAnyQuery,
  ]);

  return (
    <>
      <div className="timesheet-grid " >
        <div className="timesheet-2-cols" style={{ marginBottom: "24px", position: "relative", display: "inline-block" }}>
          <div>
            <DropdownComponent
              options={formatedEmployeesForDropdown}
              label="Colaborador"
              isValueId={true}
              fieldName="employee"
              currentObject={{
                employee: employeeIdToCheck,
              }}
              getValue={employeeIdToCheck}
              setValue={(val) => {
                setEmployeeIdToCheck(val.employee.id);
              }}
            />

            <i
              className="pi pi-info-circle cursor-pointer"
              style={{
                color: "#2196F3",
                position: "absolute",
                right: employeeIdToCheck ? "80%" : "97%",
                top: "7%",
                transform: "translateY(-50%)",
              }}
              title={`Apenas aparecerão listados colaboradores com data de rescisão a partir de ${(() => {
                const data = new Date();
                data.setMonth(data.getMonth() - 2);

                return `${String(data.getMonth() + 1).padStart(2, "0")}/${data.getFullYear()}`;
              })()
                }. Para mais informações, contactar a equipa de administração do sistema.`}
            />
          </div>
        </div>
      </div>

      {
        employeeIdToCheck !== undefined ? (
          isLoadingAnyQuery ? (
            <Spinner />
          ) : (

            <table width={"100%"} className="summary-table">
              <thead>
                <tr className="line-border">
                  <th rowSpan={2}>N.º</th>
                  <th rowSpan={2}>Colaborador</th>
                  <th colSpan={3}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      Dias Gozados até à Data de Rescisão

                      <i
                        className="pi pi-info-circle cursor-pointer"
                        style={{
                          color: "#2196F3",
                          fontSize: "0.9rem",
                        }}
                        title="Engloba dias de férias em estado 'Aprovado' e 'Cancelamento Pendente' até ao dia da rescisão (inclusive)."
                      />
                    </span>
                  </th>
                  <th colSpan={2}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      Dias que ficarão por Gozar

                      <i
                        className="pi pi-info-circle cursor-pointer"
                        style={{
                          color: "#2196F3",
                          fontSize: "0.9rem",
                        }}
                        title="Engloba dias de férias: por marcar; aqueles que se encontram em estado 'A Aprovar'; todos os que estão em estado 'Aprovado' e 'Cancelamento Pendente' após o dia da rescisão."
                      />
                    </span>
                  </th>
                  <th colSpan={3}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      Total legal de dias

                    </span>
                  </th>
                </tr>

                <tr className="line-border">
                  <th>{currentYear - 1}</th>
                  <th>{currentYear}</th>
                  <th>{currentYear + 1}</th>
                  <th>{currentYear - 1}</th>
                  <th>{currentYear}</th>
                  <th>{currentYear - 1}</th>
                  <th>{currentYear}</th>
                  <th>{currentYear + 1}</th>
                </tr>
              </thead>

              <tbody>
                {employeesInTableFormat.map((item, idx) => (
                  <tr className="line-border" key={idx}>
                    <td width={"6%"}>{item.employeeNumber}</td>
                    <td width={"20%"} align="left">{item.employeeName}</td>
                    <td width={"9.25%"}>{item.daysAlreadyTakenLastYear}</td>
                    <td width={"9.25%"}>{item.daysAlreadyTakenThisYear}</td>
                    <td width={"9.25%"}>{item.daysAlreadyTakenNextYear}</td>
                    <td width={"9.25%"}>{item.daysStillToTakeLastYear}</td>
                    <td width={"9.25%"}>{item.daysStillToTakeThisYear}</td>
                    <td width={"9.25%"}>{item.totalLegalDaysLastYear}</td>
                    <td width={"9.25%"}>{item.totalLegalDaysCurrentYear}</td>
                    <td width={"9.25%"}>{item.totalLegalDaysNextYear}</td>
                  </tr>
                ))}
              </tbody>
            </table>

          )
        ) : null
      }

    </>
  );
};
